export function countryCodeToFlagEmoji(countryCode: string): string | null {
	// Ensure the input is uppercase
	const code = countryCode.toUpperCase();

	// Validate the country code
	if (!/^[A-Z]{2}$/.test(code)) {
		return null;
	}

	// Convert the country code to flag emoji
	return [...code].map((char) => String.fromCodePoint(char.charCodeAt(0) + 0x1f1a5)).join("");
}

export const hexToRgb = (hex: string) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result
		? {
				r: Number.parseInt(result[1], 16),
				g: Number.parseInt(result[2], 16),
				b: Number.parseInt(result[3], 16),
			}
		: null;
};

export const rgbToHex = (r: number, g: number, b: number) => {
	const hex = r.toString(16) + g.toString(16) + b.toString(16);
	return `#${hex}`;
};

export const hexToHsl = (hex: string) => {
	const rgb = hexToRgb(hex);

	if (!rgb) {
		return null;
	}

	const r = rgb.r / 255;
	const g = rgb.g / 255;
	const b = rgb.b / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	const l = (max + min) / 2;
	let h = 0;
	let s = 0;

	if (max === min) {
		h = 0;
		s = 0;
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		h /= 6;
	}

	return {
		h: h * 360,
		s: s * 100,
		l: l * 100,
	};
};

export const last = <T>(arr: T[]): T | undefined => arr[arr.length - 1];

export const dedupeBy = <T>(arr: T[], callback: (value: T) => unknown): T[] => {
	const seen = new Set<unknown>();
	return arr.filter((value) => {
		const key = callback(value);
		if (seen.has(key)) {
			return false;
		}
		seen.add(key);
		return true;
	});
};
export const formatOrderDate = (dateString?: string): string => {
	if (!dateString) return "N/A";
	return new Intl.DateTimeFormat("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	}).format(new Date(dateString));
};

export const getProductName = ({
	name,
	variantName,
}: { name?: string; variantName?: string } = {}) => {
	if (!variantName) return name;

	return name?.replace(variantName, " ").trim();
};

export const isNumeric = (value: string) => !Number.isNaN(Number(value));

export const isDashedSize = (value: string) => {
	const parts = value.split("-");
	return parts.length === 2 && isNumeric(parts[0]) && isNumeric(parts[1]);
};

export const getBaseUrl = () => {
	if (typeof window !== "undefined") {
		return window.location.origin;
	}
	if (process.env.NODE_ENV === "development") {
		return "https://localhost:3000";
	}
	if (process.env.VERCEL_ENV === "production") {
		return "https://vikingfootwear.com";
	}
	return "https://vikingfootwear.com";
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const getLqipProps = <T extends { style?: React.CSSProperties } & Record<string, any>>(
	lqip: string | number | undefined,
	props?: T,
) => {
	if (!lqip)
		return {
			...props,
		};

	return {
		...props,
		style: {
			...props?.style,
			"--lqip": lqip,
		} as React.CSSProperties,
		"data-lqip": lqip,
	};
};

export const formatPrice = (price: string | number | undefined | null): string => {
	// Handle null/undefined cases
	if (price == null) return "";

	// Convert to string if it's a number
	const priceStr = String(price);

	// First, remove .00 from prices (e.g., "1650.00 NOK" -> "1650 NOK")
	const formatted = priceStr.replace(/\.00(?=\D|$)/g, "");

	// Then, handle prices with other decimals (e.g., "399.20 NOK" -> "400 NOK")
	// Extract the numeric part and currency/suffix
	const match = formatted.match(/^([\d.]+)\s*(.*)$/);
	if (!match) return formatted;

	const [, numericPart, suffix] = match;

	if (numericPart.includes(".")) {
		const numericValue = Number.parseFloat(numericPart);
		const rounded = Math.round(numericValue);
		return suffix ? `${rounded} ${suffix.trim()}` : String(rounded);
	}

	return formatted;
};

export function humanizeString(str: string) {
	if (typeof str !== "string") return "";
	const normalized = str.replace(/[-_]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
	return normalized.replace(/\b\w/g, (char) => char.toUpperCase()).trim();
}

/**
 * Resolves the display label for a single breadcrumb segment. Shared by the visual
 * breadcrumbs (PathBreadcrumbs) and the BreadcrumbList structured data so both render
 * identical labels. The leaf uses the provided title; other segments are humanized and
 * translated word-by-word, then translated as a whole (matching the <Translate> wrapper).
 */
export function resolveBreadcrumbName({
	segment,
	isLast,
	title,
	t,
}: {
	segment: string;
	isLast: boolean;
	title?: string;
	t: (key: string) => string;
}) {
	if (isLast && title) return t(title);
	const wordTranslated = humanizeString(segment)
		.split(" ")
		.map((word) => t(word))
		.join(" ");
	return t(wordTranslated);
}
